import React, { createContext, useContext, useState, useEffect } from "react";

import {
  getSupplierLogo,
  getThemePreview,
  listSupplierLogos,
  listThemePreviews,
} from "../../services/documentGeneration.service";
import { ZohoRelatedMeter } from "../../services/zoho.service";
import {
  ProposalPayload,
  SupplierLogo,
  SupplierQuote,
  ThemePreview,
} from "../../types/proposalDocuments.types";
import {
  CONTRACT_TERM_ERROR_MESSAGE,
  validateContractTerm,
} from "../../utils/proposalDocuments/validateContractTerm";

import { BASE_PAYLOAD } from "./proposalDocuments.constants";

interface ProposalDocumentContextType {
  payload: ProposalPayload;
  setPayload: React.Dispatch<React.SetStateAction<ProposalPayload>>;
  isUploadingImage: boolean;
  setIsUploadingImage: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  iframeUrl: string;
  setIframeUrl: React.Dispatch<React.SetStateAction<string>>;
  pdfJobSubmitted: boolean;
  setPdfJobSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  pdfDownloadUrl: string;
  setPdfDownloadUrl: React.Dispatch<React.SetStateAction<string>>;
  advancedOptionsEnabled: boolean;
  setAdvancedOptionsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  advancedPrintOptionsEnabled: boolean;
  setAdvancedPrintOptionsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  themePreviews: Record<string, ThemePreview>;
  previewsLoading: boolean;
  supplierLogos: SupplierLogo[];
  supplierLogosLoading: boolean;
  fetchSupplierLogo: (supplierName: string) => Promise<string | null>;
  errorMessage: string;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  zohoCustomerError: string;
  setZohoCustomerError: React.Dispatch<React.SetStateAction<string>>;
  zohoIdValid: boolean;
  setZohoIdValid: React.Dispatch<React.SetStateAction<boolean>>;
  relatedMeters: ZohoRelatedMeter[];
  setRelatedMeters: React.Dispatch<React.SetStateAction<ZohoRelatedMeter[]>>;
  metersFetched: boolean;
  setMetersFetched: React.Dispatch<React.SetStateAction<boolean>>;
  fieldErrors: Record<string, string>;
  resetForm: () => void;
  validatePayload: () => boolean;
  // Broadcast state consumed by useCollapsible, since the outline rail
  // renders as a sibling of Quotes/QuoteSection/SupplierQuoteCard and needs
  // to command their collapse state without prop-drilling through the tree
  quotesExpandRequest: {
    ids: string[];
    // When set, every collapsible whose `groupId` matches this value gets
    // force-collapsed unless its own id is in `ids` - lets a rail click on
    // one option collapse its sibling options within the same section.
    collapseSiblingsInGroup?: string;
    token: number;
  } | null;
  requestQuoteExpand: (ids: string[], collapseSiblingsInGroup?: string) => void;
  quotesBulkCommand: { action: "expand" | "collapse"; token: number } | null;
  requestQuotesBulk: (action: "expand" | "collapse") => void;
}

// Signed logo URLs expire after 1 hour server-side; cache them for slightly
// less than that so we always refetch a fresh URL before it goes dead.
const SUPPLIER_LOGO_CACHE_TTL_MS = 55 * 60 * 1000;
const SUPPLIER_LOGO_CACHE = new Map<string, { url: string; expiresAt: number }>();

const ProposalDocumentContext = createContext<ProposalDocumentContextType | null>(null);

export const ProposalDocumentProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [payload, setPayload] = useState<ProposalPayload>(BASE_PAYLOAD);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [pdfJobSubmitted, setPdfJobSubmitted] = useState(false);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState("");
  const [themePreviews, setThemePreviews] = useState<Record<string, ThemePreview>>({});
  const [previewsLoading, setPreviewsLoading] = useState(false);
  const [supplierLogos, setSupplierLogos] = useState<SupplierLogo[]>([]);
  const [supplierLogosLoading, setSupplierLogosLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [zohoCustomerError, setZohoCustomerError] = useState("");
  const [zohoIdValid, setZohoIdValid] = useState(false);
  const [relatedMeters, setRelatedMeters] = useState<ZohoRelatedMeter[]>([]);
  const [metersFetched, setMetersFetched] = useState(false);
  const [advancedOptionsEnabled, setAdvancedOptionsEnabled] = useState(false); // Configuration of copy
  const [advancedPrintOptionsEnabled, setAdvancedPrintOptionsEnabled] = useState(false); // Configuration of print output
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [quotesExpandRequest, setQuotesExpandRequest] = useState<{
    ids: string[];
    collapseSiblingsInGroup?: string;
    token: number;
  } | null>(null);
  const [quotesBulkCommand, setQuotesBulkCommand] = useState<{
    action: "expand" | "collapse";
    token: number;
  } | null>(null);

  const requestQuoteExpand = (ids: string[], collapseSiblingsInGroup?: string) => {
    setQuotesExpandRequest({ ids, collapseSiblingsInGroup, token: Date.now() });
  };

  const requestQuotesBulk = (action: "expand" | "collapse") => {
    setQuotesBulkCommand({ action, token: Date.now() });
  };

  const resetForm = () => {
    setPayload(BASE_PAYLOAD);
    setIsLoading(false);
    setPdfJobSubmitted(false);
    setPdfDownloadUrl("");
    setIframeUrl("");
    setShowModal(false);
    setErrorMessage("");
    setFieldErrors({});
    setQuotesExpandRequest(null);
    setQuotesBulkCommand(null);
    // Zoho validation gate — without this, "Start new proposal" left the
    // old customer's "Customer verified — X meters found" banner showing
    setZohoCustomerError("");
    setZohoIdValid(false);
    setRelatedMeters([]);
    setMetersFetched(false);
  };

  // Checks one proposed quote in isolation (meter selected, rates unique
  // and actually filled in). Duplicate-meter-within-section is handled by
  // the caller, since that needs visibility across sibling quotes.
  const validateQuote = (quote: SupplierQuote, sectionIdx: number, quoteIdx: number) => {
    const quoteErrors: Record<string, string> = {};
    const base = `proposed-${sectionIdx.toString()}-${quoteIdx.toString()}`;

    // Meter — exactly one of mpan/mprn, matching validate_has_meter_number
    // on the backend. Both keys are set so whichever meter-number select
    // is currently showing (mpan or mprn) picks up the message.
    const hasMpan = !!quote.mpan?.trim();
    const hasMprn = !!quote.mprn?.trim();
    if (!hasMpan && !hasMprn) {
      const msg = "Please select a meter";
      quoteErrors[`${base}-mpan`] = msg;
      quoteErrors[`${base}-mprn`] = msg;
    } else if (hasMpan && hasMprn) {
      const msg = "A quote can only have one of MPAN or MPRN set";
      quoteErrors[`${base}-mpan`] = msg;
      quoteErrors[`${base}-mprn`] = msg;
    }

    // Rates — unique labels (validate_rates_unique) and actually filled in
    if (quote.rates.length === 0) {
      quoteErrors[`${base}-rates`] = "At least one rate is required";
    }

    const seenLabels = new Set<string>();
    quote.rates.forEach((r, rateIdx) => {
      const rateKey = `${base}-rate-${rateIdx.toString()}`;
      if (seenLabels.has(r.label)) {
        quoteErrors[rateKey] = `Duplicate rate: ${r.label} is already used in this option`;
      } else if (!r.value || r.value <= 0) {
        quoteErrors[rateKey] = `${r.label}: enter a rate value`;
      } else if (r.hasConsumption && !r.nonCommodity && (!r.consumption || r.consumption <= 0)) {
        quoteErrors[rateKey] = `${r.label}: enter a consumption figure`;
      }
      seenLabels.add(r.label);
    });

    return quoteErrors;
  };

  const validatePayload = () => {
    const allErrors: Record<string, string> = {};
    // Human-readable "Section X — Option #Y: message" breakdown so errors
    // can be pinpointed without hunting through every card
    const issues: string[] = [];

    payload.quote_sections.forEach((section, sIdx) => {
      const sectionLabel =
        `Section ${(sIdx + 1).toString()}` + (section.supplier ? ` (${section.supplier})` : "");

      // Term is required unless this section is the current/rolling supplier
      if (!section.current && !validateContractTerm(section.term)) {
        allErrors[`term-${sIdx.toString()}`] = CONTRACT_TERM_ERROR_MESSAGE;
        issues.push(`${sectionLabel} — Term: ${CONTRACT_TERM_ERROR_MESSAGE}`);
      }

      // A meter can only be quoted once per section
      const meterCounts = new Map<string, number>();
      section.proposed.forEach((q) => {
        if (q.zoho_meter_id) {
          meterCounts.set(q.zoho_meter_id, (meterCounts.get(q.zoho_meter_id) ?? 0) + 1);
        }
      });

      section.proposed.forEach((quote, qIdx) => {
        const optionLabel = `${sectionLabel} — Option #${(qIdx + 1).toString()}`;
        const quoteErrors = validateQuote(quote, sIdx, qIdx);

        if (quote.zoho_meter_id && (meterCounts.get(quote.zoho_meter_id) ?? 0) > 1) {
          const dupMsg = "This meter is already used by another option in this section";
          const base = `proposed-${sIdx.toString()}-${qIdx.toString()}`;
          quoteErrors[`${base}-mpan`] = dupMsg;
          quoteErrors[`${base}-mprn`] = dupMsg;
        }

        Object.assign(allErrors, quoteErrors);

        // Some errors (e.g. missing/duplicate meter) are deliberately set
        // under both the mpan and mprn keys, so whichever dropdown is
        // currently shown displays it — dedupe by message here so the
        // summary doesn't show the same issue twice
        const uniqueMessages = new Set(Object.values(quoteErrors));
        uniqueMessages.forEach((msg) => {
          issues.push(`${optionLabel}: ${msg}`);
        });
      });
    });

    setFieldErrors(allErrors);

    const hasErrors = issues.length > 0;
    if (hasErrors) {
      setErrorMessage(issues.join("\n"));
    }

    return !hasErrors;
  };

  useEffect(() => {
    const fetchPreviews = async () => {
      setPreviewsLoading(true);
      try {
        const res = await listThemePreviews();
        const { themes } = (await res.json()) as { themes: string[] };
        const previewResponses = await Promise.all(themes.map((t) => getThemePreview(t)));
        const previews = await Promise.all(
          previewResponses.map((r) => r.json() as Promise<ThemePreview>),
        );
        const previewMap: Record<string, ThemePreview> = {};
        themes.forEach((t, i) => {
          previewMap[t] = previews[i];
        });
        setThemePreviews(previewMap);
      } catch (err) {
        console.error("Failed to fetch theme previews:", err);
      } finally {
        setPreviewsLoading(false);
      }
    };

    const fetchSupplierLogos = async () => {
      setSupplierLogosLoading(true);
      try {
        const res = await listSupplierLogos();
        const { suppliers } = (await res.json()) as { suppliers: string[] };
        const supplierLogos = suppliers
          .map((supplier): SupplierLogo => {
            const name = supplier
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");

            return {
              id: supplier,
              name,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setSupplierLogos(supplierLogos);
      } catch (err) {
        console.error("Failed to fetch theme previews:", err);
      } finally {
        setSupplierLogosLoading(false);
      }
    };

    void fetchPreviews();
    void fetchSupplierLogos();
  }, []);

  const fetchSupplierLogo = async (supplierName: string) => {
    // Return cached value if available and not past its expiry
    const cached = SUPPLIER_LOGO_CACHE.get(supplierName);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.url;
    }
    try {
      const res = await getSupplierLogo(supplierName);
      const { logo_url } = (await res.json()) as { logo_url: string };
      SUPPLIER_LOGO_CACHE.set(supplierName, {
        url: logo_url,
        expiresAt: Date.now() + SUPPLIER_LOGO_CACHE_TTL_MS,
      });
      return logo_url;
    } catch (err) {
      console.error("Failed to fetch supplier logo::", err);
      return null;
    }
  };

  return (
    <ProposalDocumentContext.Provider
      value={{
        payload,
        setPayload,
        isUploadingImage,
        setIsUploadingImage,
        isLoading,
        setIsLoading,
        showModal,
        setShowModal,
        iframeUrl,
        setIframeUrl,
        pdfJobSubmitted,
        setPdfJobSubmitted,
        pdfDownloadUrl,
        setPdfDownloadUrl,
        advancedOptionsEnabled,
        setAdvancedOptionsEnabled,
        advancedPrintOptionsEnabled,
        setAdvancedPrintOptionsEnabled,
        themePreviews,
        previewsLoading,
        supplierLogos,
        supplierLogosLoading,
        fetchSupplierLogo,
        errorMessage,
        setErrorMessage,
        zohoCustomerError,
        setZohoCustomerError,
        zohoIdValid,
        setZohoIdValid,
        relatedMeters,
        setRelatedMeters,
        metersFetched,
        setMetersFetched,
        fieldErrors,
        resetForm,
        validatePayload,
        quotesExpandRequest,
        requestQuoteExpand,
        quotesBulkCommand,
        requestQuotesBulk,
      }}
    >
      {children}
    </ProposalDocumentContext.Provider>
  );
};

export const useProposalDocument = (): ProposalDocumentContextType => {
  const ctx = useContext(ProposalDocumentContext);
  if (!ctx) throw new Error("useProposalDocument must be used within ProposalDocumentProvider");
  return ctx;
};
